<template>
  <div v-if="loading" class="flex h-full justify-center items-center">
    <loading-spinner diameter="64px" />
  </div>

  <alert-badge
    v-else-if="error.title && !hasLoaded"
    isDanger
    :title="error.title"
    :body="error.body"
    @dismissed="
      () => {
        error.title = ''
        error.body = ''
      }
    "
  />

  <div v-else ref="paymentContainer" class="w-3/6 md:w-1/3 bg-white p-5 mx-2 mt-2">
    <alert-badge
      isDanger
      :title="error.title"
      :body="error.body"
      @dismissed="
        () => {
          error.title = ''
          error.body = ''
        }
      "
    />

    <div>
      <multi-currency-payment-input
        v-model:total-paid="orderPayment.totalPaidAmount"
        v-model:change-due="orderPayment.changeDueAmount"
        v-model:total-paid-in-cash-before-change="orderPayment.reqOnlyTotalCashPaidAmount"
        v-model:total-paid-in-epayment="orderPayment.reqOnlyTotalEpaymentAmount"
        v-model:total-paid-in-credit="orderPayment.reqOnlyTotalCreditPaidAmount"
        v-model:cash-multi-currency-breakdown="orderPayment.reqOnlyCashMultiCurrencyBreakdown"
        :customerID="customerID"
        :total-due="orderPayment.totalDueAmount"
        :available-terminals="availableTerminals"
        :terminal-error="terminalError"
        @update:terminalID="(val) => { orderPayment.terminalID = val; terminalError = false }"
        @update:lastFourDigits="(val) => orderPayment.lastFourDigits = val"
      >
        <template v-if="fullOrder" #before-total-due>
          <div class="flex justify-between items-center text-base font-bold mx-5" :dir="$ctx.getDir()">
            <div class="">{{ t('orderTotal') }}</div>
            <div class="flex grow justify-end">
              <div dir="ltr" class="w-1/2 p-1">{{ formatDinero(fullOrder.computeTotal()) }}</div>
            </div>
          </div>
          <div class="flex justify-between items-center text-base font-bold mx-5" :dir="$ctx.getDir()">
            <div class="flex items-center gap-2">
              <span>{{ t('totalDiscount') }}</span>
              <span @click="openDiscountDialog" class="cursor-pointer">
                <img src="../../assets/discountTag.svg" alt="discount icon" />
              </span>
            </div>
            <div class="flex grow justify-end">
              <div v-if="displayDiscountAmount > 0" dir="ltr" class="text-red-400 w-1/2 p-1 me-8">
                - {{ formatDinero(displayDiscountDinero) }}
              </div>
            </div>
          </div>
        </template>
      </multi-currency-payment-input>
    </div>

    <div class="mt-3" v-if="settingsStore.getValue('money_collector_required') === 'true' || moneyCollectors.length > 0">
      <div>
        <span v-if="settingsStore.getValue('money_collector_required') === 'true'" class="text-red-500">* </span>
        {{ t('moneyCollector') }}
      </div>
      <div>
        <el-select
          v-model="orderPayment.moneyCollectorID"
          class="w-full"
          filterable
          :clearable="settingsStore.getValue('money_collector_required') !== 'true'"
          :placeholder="t('selectMoneyCollector')"
        >
          <el-option
            v-for="collector in moneyCollectors"
            :key="collector.id"
            :label="collector.name"
            :value="collector.id"
          />
        </el-select>
      </div>
    </div>

    <div class="mt-1">
      <div>{{ t('notes') }}</div>
      <div>
        <el-input v-model="orderPayment.notes" :rows="2" type="textarea" placeholder="" />
      </div>
    </div>

    <div class="flex justify-between items-center mb-2 mt-2">
      <horizontal-button :title="t('placePay')" isSuccess @click="openCashierPinDialog" />
    </div>
  </div>

  <el-dialog v-model="cashierPinDialogOpen" width="30%" @close="cashierPinDialogOpen = false">
    <div>
      <!-- Enter the pin -->
      <div class="my-2">
        <p>{{ t('enterCashierPin') }}</p>
      </div>

      <!-- password input up to 6 chars -->
      <div class="my-2">
        <el-input
          id="password"
          v-model="cashierPin"
          type="password"
          required
          show-password
          autofocus
          dir="ltr"
          :maxlength="4"
          :minlength="4"
          ref="cashierPinInput"
          @keyup.enter="submitPayment"
        />
      </div>

      <!-- finish payment -->
      <div class="flex items-center justify-between mb-2">
        <horizontal-button
          class="text-lg complete-payment"
          :title="t('placePay')"
          @click="submitPayment"
          :rounded="false"
        />
      </div>
    </div>
  </el-dialog>

  <el-dialog v-if="fullOrder" v-model="discountDialogOpen" width="30%">
    <div class="w-full flex flex-col items-center justify-center" :dir="$ctx.getDir()">
      <div class="w-full flex items-center justify-between">
        <h1 class="text-l font-bold">{{ t('subTotal') }}</h1>
        <h1 class="text-l font-bold">{{ formatDinero(fullOrder.computeTotal()) }}</h1>
      </div>

      <el-divider />

      <el-radio-group class="w-full flex mb-4" v-model="fullOrder.discountType">
        <el-radio-button class="flex-1" label="percentage" border>
          {{ t('percentage') }}
        </el-radio-button>
        <el-radio-button class="flex-1" label="amount" border>
          {{ t('amount') }}
        </el-radio-button>
      </el-radio-group>

      <div
        v-if="fullOrder.discountType === 'percentage'"
        class="mb-4 w-full flex items-center justify-between"
      >
        <h1 class="text-l font-bold">{{ t('percentage') }}</h1>
        <div class="flex items-center">
          <span class="mx-6 text-l font-bold">%</span>
          <el-input-number
            v-model="fullOrder.discountByPercentage"
            :step="1"
            size="medium"
            :min="0"
            :controls="false"
            ref="discountPercentageInput"
          />
        </div>
      </div>

      <div
        v-if="fullOrder.discountType === 'amount'"
        class="mb-6 w-full flex items-center justify-between"
      >
        <h1 class="text-l font-bold">{{ t('amount') }}</h1>
        <div class="flex items-center">
          <money-input
            class="mx-8"
            :currency="$ctx.currency"
            v-model="fullOrder.discountByAmount"
            ref="discountAmountInput"
          />
        </div>
      </div>

      <div class="w-full flex items-center justify-between bg-gray-200 p-4">
        <h1 class="text-l font-bold">{{ t('total') }}</h1>
        <h1 class="text-blue-500 text-l font-bold">
          {{ formatDinero(fullOrder.computeTotalAfterDiscount()) }}
        </h1>
      </div>
    </div>
    <template #footer>
      <span class="dialog-footer">
        <el-button type="primary" class="bg-primary" @click="applyDiscount">
          {{ t('confirm') }}
        </el-button>
        <el-button type="danger" @click="cancelDiscount">
          {{ t('cancelDiscount') }}
        </el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script lang="ts">
import { defineComponent, onBeforeMount, nextTick, ref, computed, watch, onMounted } from 'vue'

import LoadingSpinner from '@/components/LoadingSpinner.vue'
import AlertBadge from '@/components/AlertBadge.vue'
import HorizontalButton from '@/components/HorizontalButton.vue'
import MoneyInput from '@/components/form/MoneyInput.vue'

import { useI18n } from 'vue-i18n'
import { CustomerInvoice, CustomerInvoicePayment } from '@/models/sales/CustomerInvoice'
import { useHTTP } from '@/plugins/http'
import { useAlertModal } from '@/plugins/alert-modal/alert-modal'
import { useRouter } from 'vue-router'
import { Route } from '@/router/routes'
import { Transaction } from '@/models/accounting/Transaction'
import MultiCurrencyPaymentInput from '@/components/money/MultiCurrencyPaymentInput.vue'
import {useSettingsStore} from "@/stores/settingsStore";
import {ElMessageBox, ElLoading} from "element-plus";
import {Order} from "@/models/sales/Order";
import User from '@/models/user/User';
import { formatDinero } from '@/utils/money'
import { useContext } from '@/plugins/context'
import Dinero from 'dinero.js'
import type { Currency } from 'dinero.js'
import { Register } from '@/models/company/Register'

export default defineComponent({
  name: 'order-payment',

  components: {
    MultiCurrencyPaymentInput,
    LoadingSpinner,
    AlertBadge,
    HorizontalButton,
    MoneyInput,
  },

  setup() {
    const http = useHTTP()
    const alertModal = useAlertModal()
    const router = useRouter()
    const settingsStore = useSettingsStore()
    const ctx = useContext()
    const params = router.currentRoute.value.params
    const loading = ref(false)
    const hasLoaded = ref(false)
    const cashierPinDialogOpen = ref(false)
    const cashierPin = ref('')
    const isSubmitting = ref(false)
    const cashierPinInput = ref()
    const paymentInput2 = ref(null)
    const paymentContainer = ref<HTMLElement | null>(null)
    let invoice: CustomerInvoice
    // The invoice's customer — drives the "Credit" tab (pay from store credit).
    const customerID = ref('')
    const orderPayment = ref(new CustomerInvoicePayment())
    orderPayment.value.changeDueAmount = 0
    orderPayment.value.paidBeforeChangeAmount = 0
    const error = ref({
      title: '',
      body: ''
    })

    const moneyCollectors = ref<User[]>([])
    const availableTerminals = ref<any[]>([])
    const terminalError = ref(false)

    // Discount state
    const fullOrder = ref<Order | null>(null)
    const discountDialogOpen = ref(false)
    let isApplyingDiscount = false
    const discountAmountInput = ref()
    const discountPercentageInput = ref()
    const displayDiscountAmount = ref(0)

    const displayDiscountDinero = computed(() =>
      Dinero({
        amount: displayDiscountAmount.value,
        currency: (invoice?.currency as Currency) || 'IQD',
        precision: invoice?.precision || 0
      })
    )

    // Saved state for cancel
    let _savedDiscountByAmount = 0
    let _savedDiscountByPercentage = 0
    let _savedDiscountType: 'amount' | 'percentage' = 'amount'

    const { t } = useI18n({
      messages: {
        en: {
          successMsg: 'Payment made successfully',
          errInvalidAmount: 'Invalid amount. Must be bigger > 0',
          errTerminalRequired: 'A terminal must be selected when using E-Payment',
          errPaymentIsComplete: 'Payments are already complete, nothing to pay',
          placePay: 'Place payment',
          enterCashierPin: 'Enter cashier pin',
          errCashierPinRequired: 'Cashier pin is required',
          errMoneyCollectorRequired: 'Money collector is required',
          markAsDelivered: 'Order is fully paid, do you want to mark it as delivered?',
          moneyCollector: 'Money Collector',
          selectMoneyCollector: 'Select money collector',
          notes: 'Notes',
          orderTotal: 'Order Total',
          totalDiscount: 'Total Discount',
          subTotal: 'Sub Total',
          total: 'Total',
          percentage: 'Percentage',
          amount: 'Amount',
          discountRequiresEdit: 'This discount would require a refund. Would you like to go to the edit page to apply it?',
          goToEditPage: 'Go to Edit Page',
          applyingDiscount: 'Applying discount...',
          confirm: 'Confirm',
          cancelDiscount: 'Cancel Discount',
        },
        ar: {
          successMsg: 'تم الدفع بنجاح',
          errInvalidAmount: 'يجب ان يكون المبلغ المدفوع اكبر من 0',
          errTerminalRequired: 'يجب اختيار جهاز عند استخدام الدفع الإلكتروني',
          errPaymentIsComplete: 'تم دفع المبلغ كاملا',
          placePay: 'اتمام الدفع',
          enterCashierPin: 'ادخل رمز الكاشير الخاص بك',
          errCashierPinRequired: 'يرجى ادخال رمز الكاشير لاتمام البيع',
          errMoneyCollectorRequired: 'جابي الأموال مطلوب',
          markAsDelivered: 'تم دفع الطلب بالكامل، هل تريد وضع علامة عليه بأنه تم التسليم؟',
          moneyCollector: 'الجابي',
          selectMoneyCollector: 'اختر جابي الأموال',
          notes: 'ملاحظات',
          orderTotal: 'إجمالي الطلب',
          totalDiscount: 'إجمالي التخفيض',
          subTotal: 'المبلغ الإجمالي',
          total: 'المجموع الكلي',
          percentage: 'الخصم بالنسبة',
          amount: 'الخصم بالمبلغ',
          discountRequiresEdit: 'هذا التخفيض يتطلب استرداد مبلغ. هل تريد الذهاب إلى صفحة التعديل لتطبيقه؟',
          goToEditPage: 'الذهاب إلى صفحة التعديل',
          applyingDiscount: 'جاري تطبيق التخفيض...',
          confirm: 'التأكيد',
          cancelDiscount: 'الغاء التخفيض',
        }
      }
    })

    onBeforeMount(async () => {
      try {
        loading.value = true
        const invoiceRes = await http.get<CustomerInvoice>(
          `${CustomerInvoice.ENDPOINT}/${params.id}`
        )

        invoice = CustomerInvoice.from(invoiceRes)
        customerID.value = (invoice.customerID as string) || invoice.customer?.id || ''

        orderPayment.value.currency = invoice.currency
        orderPayment.value.precision = invoice.precision
        orderPayment.value.customerInvoiceID = invoice.id

        const paymentRemaining = invoice.totalBalanceAmount
        orderPayment.value.totalDueAmount = paymentRemaining
        if (paymentRemaining === 0) {
          alertModal.showDanger({
            title: t('errPaymentIsComplete')
          })

          await router.push(
            Route.SALES_ORDERS_ORDER.replace(':id', invoice.referenceableID as string)
          )
        }

        // Load full order for discount functionality
        try {
          const orderRes = await http.get<Order>(`${Order.ENDPOINT}/${invoice.referenceableID}`)
          fullOrder.value = Order.from(orderRes)
          fullOrder.value.customerID = fullOrder.value.invoice.customer?.id
          // Authoritative customer id for the credit tab (this fetch preloads the customer).
          customerID.value =
            (fullOrder.value.invoice.customerID as string) ||
            fullOrder.value.invoice.customer?.id ||
            customerID.value
          fullOrder.value.paymentType = fullOrder.value.invoice.paymentType
          fullOrder.value.discountType = fullOrder.value.discountType || 'amount'
          if (fullOrder.value.invoice.totalDiscountAmount) {
            fullOrder.value.discountByAmount = fullOrder.value.invoice.totalDiscountAmount
            displayDiscountAmount.value = fullOrder.value.invoice.totalDiscountAmount
          }
        } catch (err) {
          console.error('Failed to load order for discount', err)
        }

        // Load money collectors (users with "Money Collector" tag)
        try {
          const usersRes = await http.get<User[]>(User.ENDPOINT+'/money-collectors')
          moneyCollectors.value = usersRes
            .map(user => User.from(user))
            .filter(user => user.extraProperties?.tags?.includes('Money Collector'))
        } catch (err) {
          console.error('Failed to load money collectors', err)
        }

        // Load enabled terminals for current register (fall back to order's registerID)
        try {
          const registerID = ctx.currentRegister?.id || fullOrder.value?.registerID
          if (registerID) {
            const rts = await http.get<any[]>(`${Register.ENDPOINT}/${registerID}/terminals`)
            availableTerminals.value = rts || []
          }
        } catch (err) {
          console.error('Failed to load terminals', err)
        }
      } catch (err) {
        error.value.title = err.title
        error.value.body = err.body
        loading.value = false
        hasLoaded.value = true
      } finally {
        loading.value = false
        hasLoaded.value = true
      }
    })

    async function submitPayment() {
      if (useSettingsStore().getValue('is_cashier_pin_required') == 'true') {
        if (cashierPin.value === '') {
          cashierPinDialogOpen.value = false
          error.value.title = t('errCashierPinRequired')
          return
        }
      }

      // Check if money collector is required
      if (useSettingsStore().getValue('money_collector_required') === 'true') {
        if (!orderPayment.value.moneyCollectorID) {
          cashierPinDialogOpen.value = false
          error.value.title = t('errMoneyCollectorRequired')
          return
        }
      }

      // Prevent double submission
      if (isSubmitting.value) {
        return
      }

      // if (orderPayment.value.totalPaidAmount === undefined) {
      //   return
      // }
      orderPayment.value.totalPaidAmount =
        orderPayment.value.reqOnlyTotalCashPaidAmount +
        orderPayment.value.reqOnlyTotalEpaymentAmount +
        orderPayment.value.reqOnlyTotalCreditPaidAmount

      if (
        orderPayment.value.reqOnlyTotalCashPaidAmount <= 0 &&
        orderPayment.value.reqOnlyTotalEpaymentAmount <= 0 &&
        orderPayment.value.reqOnlyTotalCreditPaidAmount <= 0
      ) {
        cashierPinDialogOpen.value = false
        error.value.title = t('validation.inputErrors')
        error.value.body = t('errInvalidAmount')
        return
      }

      if (orderPayment.value.reqOnlyTotalEpaymentAmount > 0 && availableTerminals.value.length > 0 && !orderPayment.value.terminalID) {
        cashierPinDialogOpen.value = false
        terminalError.value = true
        return
      }

      isSubmitting.value = true
      let tx: Transaction[]
      try {
        tx = await http.post<Transaction[]>(
          `${CustomerInvoicePayment.ENDPOINT}/payments`,
          orderPayment.value,
          { headers: { pin: cashierPin.value } }
        )

        alertModal.showSuccess({
          title: t('successMsg')
        })

        // re-fetch order and check balance amount
        const order = await http.get<Order>(
            `${Order.ENDPOINT}/${invoice.referenceableID}`
        )

        if (order.invoice.totalBalanceAmount == 0 && order.orderStatus != 'delivered') {
          order.orderStatus = 'delivered'
          ElMessageBox.confirm(t('markAsDelivered'), t('dialogs.warning'), {
            confirmButtonText: t('dialogs.confirm'),
            cancelButtonText: t('dialogs.cancel'),
            type: 'warning'
          }).then(() => {
            http.post<Order>(
                `${Order.ENDPOINT}/change-order-status/${invoice.referenceableID}`,
                order, {headers: {pin: cashierPin.value}}
            )
          }).catch(() => {
            console.log('cancelled')
          })
        }

        await router.push(
          Route.SALES_TRANSACTIONS_RECEIPT.replace(
            ':transactionID',
            tx.map((t) => t.id).join('&') || ''
          )
        )
      } catch (err) {
        error.value.title = err.title
        error.value.body = err.body
        cashierPinDialogOpen.value = false
      } finally {
        isSubmitting.value = false
      }
    }

    function openCashierPinDialog() {
      if (useSettingsStore().getValue('is_cashier_pin_required') == 'true') {
        cashierPinDialogOpen.value = true
        nextTick(() => {
          setTimeout(() => {
            cashierPinInput.value.focus()
          }, 0)
        })
        return;
      }
      submitPayment();
    }

    // When dialog is dismissed via ESC/backdrop (not via applyDiscount), restore old values
    watch(discountDialogOpen, (open) => {
      if (!open && !isApplyingDiscount) {
        cancelDiscount()
      }
    })

    function openDiscountDialog() {
      if (!fullOrder.value) return
      _savedDiscountByAmount = fullOrder.value.discountByAmount
      _savedDiscountByPercentage = fullOrder.value.discountByPercentage
      _savedDiscountType = fullOrder.value.discountType || 'amount'
      discountDialogOpen.value = true
      fullOrder.value.discountType = fullOrder.value.discountType || 'amount'
      setTimeout(() => {
        if (fullOrder.value?.discountType === 'amount') {
          const input = discountAmountInput.value?.$el.querySelector('input')
          input?.focus()
          input?.select()
        }
      }, 100)
    }

    function cancelDiscount() {
      discountDialogOpen.value = false
      if (!fullOrder.value) return
      fullOrder.value.discountByAmount = _savedDiscountByAmount
      fullOrder.value.discountByPercentage = _savedDiscountByPercentage
      fullOrder.value.discountType = _savedDiscountType
    }

    async function applyDiscount() {
      if (!fullOrder.value) return
      isApplyingDiscount = true

      const newTotalDinero = fullOrder.value.computeTotalAfterDiscount()
      const newTotal = newTotalDinero.getAmount()
      const newDiscountAmount = fullOrder.value.totalDiscountAmount
      const newDiscountByAmount = fullOrder.value.discountByAmount
      const newDiscountByPercentage = fullOrder.value.discountByPercentage
      const newDiscountType = fullOrder.value.discountType
      const alreadyPaid = invoice.totalPaidAmount

      discountDialogOpen.value = false

      if (newTotal > alreadyPaid) {
        // Discount keeps balance positive — apply directly
        const loadingInstance = ElLoading.service({ target: paymentContainer.value!, text: t('applyingDiscount'), background: 'rgba(255, 255, 255, 0.9)' })
        try {
          // Build a discount-only payload: no customerInvoicePayments and no reqOnly amounts
          // to avoid the backend creating duplicate payment records.
          const newBalance = newTotal - invoice.totalPaidAmount
          const discountPayload = {
            userID: ctx.currentUser.id || '',
            registerID: ctx.currentRegister.id || '',
            locationID: ctx.currentLocation.id || '',
            invoice: {
              pouchID: fullOrder.value.invoice.pouchID,
              salesRepID: fullOrder.value.invoice.salesRepID,
              customerID: fullOrder.value.customerID,
              currency: fullOrder.value.currency,
              precision: fullOrder.value.precision,
              totalBeforeDiscounts: fullOrder.value.totalBeforeDiscountAmount,
              totalDiscountAmount: fullOrder.value.totalDiscountAmount,
              totalDueAmount: newTotal,
              totalBalanceAmount: newBalance,
              billDueDate: fullOrder.value.invoice.billDueDate ?? null,
              paymentType: fullOrder.value.paymentType,
              customerInvoicePayments: [
                {
                  currency: fullOrder.value.currency,
                  precision: fullOrder.value.precision,
                  totalDueAmount: 0,
                  paidBeforeChangeAmount: 0,
                  changeDueAmount: 0,
                  totalPaidAmount: 0,
                }
              ],
              transactions: [],
              reqOnlyTotalCashPaidAmount: null,
              reqOnlyTotalEpaymentAmount: null,
              reqOnlyTotalCreditPaidAmount: null,
              reqOnlyCashMultiCurrencyBreakdown: null,
              notes: fullOrder.value.invoice.notes,
              carrierID: fullOrder.value.invoice.carrierID,
              shippingFeeOnUs: fullOrder.value.invoice.shippingFeeOnUs,
              shippingFeeAmount: fullOrder.value.invoice.shippingFeeAmount,
              insuranceID: fullOrder.value.invoice.insuranceID,
              insuranceTotalDueAmount: fullOrder.value.invoice.insuranceTotalDueAmount,
              insuranceTotalBalanceAmount: fullOrder.value.invoice.insuranceTotalBalanceAmount,
              insuranceTotalPaidAmount: fullOrder.value.invoice.insuranceTotalPaidAmount,
            },
            orderItems: fullOrder.value.getOrderItemForPayload(),
            orderChanges: null,
            orderType: fullOrder.value.orderType,
            requestOnlyRefundAmountInIQD: 0,
            requestOnlyRefundAmountInEpayment: 0,
            requestOnlyRefundAmountInUSD: 0,
            requestOnlyRefundAmountInCredit: 0,
            requestOnlyQuoteID: fullOrder.value.requestOnlyQuoteID,
          }
          await http.put<Order>(`${Order.ENDPOINT}/${fullOrder.value.id}`, discountPayload)

          // Re-fetch invoice to update totalDueAmount for payment
          const invoiceRes = await http.get<CustomerInvoice>(`${CustomerInvoice.ENDPOINT}/${params.id}`)
          invoice = CustomerInvoice.from(invoiceRes)
          orderPayment.value.totalDueAmount = invoice.totalBalanceAmount

          // Update discount display
          displayDiscountAmount.value = newDiscountAmount
          isApplyingDiscount = false
          loadingInstance.close()
        } catch (err) {
          error.value.title = err.title
          error.value.body = err.body
          isApplyingDiscount = false
          loadingInstance.close()
        }
      } else {
        isApplyingDiscount = false
        // Discount would cause refund — prompt to go to edit page
        ElMessageBox.confirm(t('discountRequiresEdit'), t('dialogs.warning'), {
          confirmButtonText: t('goToEditPage'),
          cancelButtonText: t('dialogs.cancel'),
          type: 'warning'
        }).then(() => {
          const query: Record<string, string> = { discountType: newDiscountType || 'amount' }
          if (newDiscountType === 'percentage') {
            query.discountValue = String(newDiscountByPercentage)
          } else {
            query.discountValue = String(newDiscountByAmount)
          }
          router.push({
            path: Route.SALES_ORDERS_ORDER_EDIT.replace(':id', fullOrder.value!.id),
            query,
          })
        }).catch(() => {
          // User cancelled prompt — restore and re-open
          cancelDiscount()
          discountDialogOpen.value = true
        })
      }
    }

    return {
      t,
      orderPayment,
      customerID,
      submitPayment,
      error,
      loading,
      hasLoaded,
      cashierPin,
      cashierPinDialogOpen,
      cashierPinInput,
      openCashierPinDialog,
      paymentInput2,
      paymentContainer,
      moneyCollectors,
      availableTerminals,
      terminalError,
      settingsStore,
      fullOrder,
      discountDialogOpen,
      discountAmountInput,
      discountPercentageInput,
      displayDiscountAmount,
      displayDiscountDinero,
      openDiscountDialog,
      cancelDiscount,
      applyDiscount,
      formatDinero,
    }
  },
})
</script>

<style></style>
